import React, { useState, Fragment, useCallback, useMemo } from 'react';
import { featureAvaliability } from '@web-3d-tool/shared-logic';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import ImageFrame from '../../ImageFrame';
import styles from './ImageFrameManipulation2DRotation.module.css';
import Drawer2DRotation from '../Drawers/drawer2DRotation/drawer2DRotation';
import EmptyPanel from '../../ImageFramesContainer/emptyPanel';
import EnlargeFrameButton from '../../ImageFramesContainer/enlagreFrameButton';
import ImagePinButton from '../../ImagePinButton/imagePinButton.js';

const ImageFrameManipulation2DRotation = ({
  is360,
  width,
  height,
  top,
  left,
  scale,
  onChange,
  isVertical,
  componentToRender,
  closestPhotoObjectParams,
  sourcesArr,
  defaultContrast,
  defaultBrightness,
  isEnlargedFrame,
  luminaOriginalImageSize,
  elementOriginalImageSize,
  viewer360Align2DImages,
  isDrawerDisabled,
  setslidersValues,
  drawerWidth,
  frameSize,
  isReady,
  toggleEnlarge,
  isDebugEnabled,
  setEmptyPanelRef,
  modelType,
  containerSize,
  isAOHS,
  sdk,
  isModelCompared,
}) => {
  const emptyPanelFixedWidth = 340;

  // memos
  const shouldRenderPinButton = useMemo(() => featureAvaliability.getIsPinIocNiriRotationButtonEnabled(), []);

  // states
  const [brightness, setBrightness] = useState(defaultBrightness);
  const [contrast, setContrast] = useState(defaultContrast);
  const [imageZindex, setimageZindex] = useState({
    niri: { zIndex: 1 },
    color: { zIndex: 1 },
  });

  // callbacks
  const handleChangeBrightness = useCallback(
    (value, type) => {
      const typeBrightness = {};
      typeBrightness[type] = value;
      setBrightness({ ...brightness, ...typeBrightness });
    },
    [brightness]
  );

  const handleChangeContrast = useCallback(
    (value, type) => {
      const typeContrast = {};
      typeContrast[type] = value;
      setContrast({ ...contrast, ...typeContrast });
    },
    [contrast]
  );

  const handleOnImageClick = useCallback(
    (type) => {
      const imagesZindex = {
        niri: { zIndex: 1 },
        color: { zIndex: 1 },
      };
      imagesZindex[type] = { zIndex: 2 };
      setimageZindex(imagesZindex);
    },
    [setimageZindex]
  );

  return (
    <>
      <div className={styles.container} style={{ flexDirection: isVertical ? 'row' : 'column' }}>
        {sourcesArr.map(({ src, type, url, isPlaceHolderIcon }, index) => (
          <Fragment key={index}>
            {(isEnlargedFrame === null || isEnlargedFrame === type) && (
              <>
                {!isPlaceHolderIcon ? (
                  <>
                    {isDebugEnabled && index === 0 && <div className={styles.imgName}>{url}</div>}
                    <ImageFrame
                      is360={is360}
                      width={width}
                      height={height}
                      src={src}
                      brightness={brightness[type]}
                      contrast={contrast[type]}
                      closestPhotoObjectParams={closestPhotoObjectParams}
                      onChange={onChange}
                      scale={scale}
                      top={top}
                      left={left}
                      id={`${type}-image`}
                      dataBiType={type}
                      isEnlargedFrame={isEnlargedFrame}
                      type={type}
                      luminaOriginalImageSize={luminaOriginalImageSize}
                      elementOriginalImageSize={elementOriginalImageSize}
                      viewer360Align2DImages={viewer360Align2DImages}
                      numberOfItems={sourcesArr.length}
                      handleOnImageClick={handleOnImageClick}
                      imageZindex={imageZindex[type]}
                      EnlargeFrameButton={EnlargeFrameButton}
                      index={index}
                      toggleEnlarge={toggleEnlarge}
                      modelType={modelType}
                    />
                    {componentToRender}
                  </>
                ) : (
                  <EmptyPanel
                    type={type}
                    src={src}
                    is360={is360}
                    isReady={isReady}
                    frameSize={{ ...frameSize, width: emptyPanelFixedWidth }}
                    drawerWidth={drawerWidth}
                    setEmptyPanelRef={setEmptyPanelRef}
                    index={index}
                  />
                )}
              </>
            )}
          </Fragment>
        ))}
      </div>
      <Drawer2DRotation
        width={width}
        sourcesArr={sourcesArr}
        isEnlargedFrame={isEnlargedFrame}
        isDrawerDisabled={isDrawerDisabled}
        handleChangeBrightness={handleChangeBrightness}
        handleChangeContrast={handleChangeContrast}
        setslidersValues={setslidersValues}
        containerSize={containerSize}
        isAOHS={isAOHS}
        modelType={modelType}
        isModelCompared={isModelCompared}
        shouldRenderPinButton={shouldRenderPinButton}
        sdk={sdk}
      />
      {shouldRenderPinButton && <ImagePinButton sdk={sdk} isModelCompared={isModelCompared} />}
    </>
  );
};

ImageFrameManipulation2DRotation.defaultProps = {
  viewer360Align2DImages: true,
  defaultContrast: { niri: 100, color: 100 },
  defaultBrightness: { niri: 100, color: 100 },
  isVertical: false,
  toggleEnlarge: noop,
};

ImageFrameManipulation2DRotation.propTypes = {
  /**
   * The image width
   */
  width: PropTypes.number,
  /**
   * The image height
   */
  height: PropTypes.number,
  /**
   * Image source
   */
  src: PropTypes.string.isRequired,
  /**
   * The rotation to present the images
   */
  rotation: PropTypes.number,
  /**
   * Set the position of the Sliders to below ImageFrame
   */
  isVertical: PropTypes.bool,
  /**
   * Callback, fired when pinch, zoom, pan changes
   */
  onChange: PropTypes.func,
  /**
   * Image id
   */
  id: PropTypes.string.isRequired,

  /**
   *  defaultBrightnesss
   */
  defaultBrightnesss: PropTypes.number,

  /**
   *  defaultContrast
   */
  defaultContrast: PropTypes.number,
  /**
   *  onBrightnessChanged
   */
  onBrightnessChanged: PropTypes.func,
  /**
   *  onContrastChanged
   */
  onContrastChanged: PropTypes.func,
  /**
   * having 360 style
   */
  is360: PropTypes.bool,
};

export default ImageFrameManipulation2DRotation;
